import * as vp from 'vopay';
import { Box, Pagination, GetColorStatus, capitalizeFirstLetterOfWords, formattedCurrency, usePagination } from 'vopay';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useState, useEffect } from 'react';
import { format, subYears } from 'date-fns';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useHostNavigate } from '../HostNavigationContext';
import useUpdateHostQueryParams from '#shared/hooks/useUpdateHostQueryParams';
import useFetchAccountTransferSchedules from '#shared/hooks/useApi/useFetchAccountTransferSchedules';
import { useI18nLabels } from '#shared/hooks/useI18nLabels';
import ClientAccountTransfersForm from './ClientAccountTransfersForm';
import ActivityType, { ActivityTabType } from './ActivityType';
import type { ActivitySubTableProps } from './TransactionHistoryTable';
import styles from './ScheduledClientAccountTransfersTable.module.scss';

import { Skeleton, Table, TableFiltersContainer, TransactionStatus } from '#shared/components/UI';
import toDayTimestamp from './toDayTimestamp';

type QueryParams = {
  status?: string;
  transferType?: string;
  frequency?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  activityType?: ActivityTabType;
};

type Filters = {
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  frequency: string;
  transferType: string;
};

type TransactionParams = Filters & {
  hasInvalidEndDate: boolean;
  offset?: number;
};

// The portal's Customer Activity → Scheduled Client Account Transfers table,
// adapted for the contact drawer/portal pages: filters live in memory
// (mirrored to the URL when persistFiltersInUrl) and the schedule-details
// link-out goes through the host's onNavigate (plain text when the host
// can't navigate).
const ScheduledClientAccountTransfersTable: FunctionComponent<ActivitySubTableProps> = ({
  clientAccountId,
  clientAccountsEnabled,
  activityTypeOptions,
  currentType,
  handleCurrentType,
  persistFiltersInUrl,
}) => {
  const alertDialog = useAlertDialog();
  const hostNavigate = useHostNavigate();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccounts.Schedule' });
  const accountIDType = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [filteredList, setFilteredList] = useState<vp.AccountTransferSchedule[]>([]);
  const { fetching, fetchAccountTransferSchedules, totalRecords } = useFetchAccountTransferSchedules();
  const { frequencyLabelMapper, transferTypeLabelMapper } = useI18nLabels();

  const initialFilters: Filters = {
    status: '',
    searchTerm: '',
    startDate: format(subYears(new Date(), 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    frequency: '',
    transferType: '',
  };

  // Portal mode seeds the filters from the page URL (deep links, reload/back
  // restoration); the drawer starts from the in-memory defaults.
  const [filters, setFilters] = useState<Filters>(() => {
    if (!persistFiltersInUrl) return initialFilters;

    const urlParams = new URLSearchParams(window.location.search);

    return {
      status: urlParams.get('status') || '',
      searchTerm: urlParams.get('searchTerm') || '',
      startDate: urlParams.get('startDate') || initialFilters.startDate,
      endDate: urlParams.get('endDate') || initialFilters.endDate,
      frequency: urlParams.get('frequency') || '',
      transferType: urlParams.get('transferType') || '',
    };
  });

  const updateQueryParams = useUpdateHostQueryParams<QueryParams>();

  const filtersApplied = Boolean(
    filters.transferType ||
      filters.status ||
      filters.frequency ||
      filters.searchTerm ||
      filters.startDate !== initialFilters.startDate ||
      filters.endDate !== initialFilters.endDate,
  );

  // pagination
  const PER_PAGE = 20;

  const { pageCount, pageIndex, setPageIndex } = usePagination(totalRecords, {
    pageSize: PER_PAGE,
  });

  const fetchScheduledTransactionHistory = async ({
    startDate,
    endDate,
    status,
    hasInvalidEndDate,
    searchTerm,
    frequency,
    transferType,
    offset = 0,
  }: TransactionParams) => {
    const scheduledTransactionHistoryRequest = {
      StartDate: startDate,
      EndDate: endDate,
      SearchText: searchTerm,
      ...(transferType === 'all' || transferType === '' ? {} : { TransferType: transferType }),
      ...(frequency === 'all' || frequency === '' ? {} : { TransferFrequency: frequency }),
      ...(status === 'all' || status === '' ? {} : { TransferStatus: status }),
      Limit: PER_PAGE,
      Offset: offset,
      [accountIDType]: clientAccountId,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);

      return;
    }

    try {
      const res = await fetchAccountTransferSchedules(scheduledTransactionHistoryRequest);

      const scheduledTransactionRequest = Object.values(
        (res.ScheduledClientAccountTransfers as { [key: number]: vp.AccountTransferSchedule }) || {},
      );

      setFilteredList(scheduledTransactionRequest);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('FetchScheduledTransactionsError.defaultMessage', {
              defaultValue: 'An error occurred while fetching eLinx history',
            });

      alertDialog.error(t('FetchScheduledTransactionsError.title', { defaultValue: 'Error' }), msg);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);

    fetchScheduledTransactionHistory({
      ...filters,
      hasInvalidEndDate: false,
      offset,
    });

    if (persistFiltersInUrl) {
      updateQueryParams({
        activityType: 'scheduledClientAccountTransfers',
      });
    }
  }, []);

  useEffect(() => {
    setHasInvalidEndDate(filters.startDate > filters.endDate);
  }, [filters.startDate, filters.endDate]);

  const tableProps: vp.TableProps<vp.AccountTransferSchedule> = {
    id: 'scheduled-transactions-list',
    version: '2',
    columns: [
      {
        id: 'id',
        header: t('Table.id', { defaultValue: 'ID' }),
        align: 'left',
        template: ({ data }) => (
          <>
            {/* Link-out via the host's onNavigate; plain text when the host can't navigate. */}
            {hostNavigate ? (
              <button
                type="button"
                className={styles.linkOut}
                onClick={() => hostNavigate(`/client-account-schedule/details/${data.ID}`)}
              >
                {data.ID}
              </button>
            ) : (
              <span>{data.ID}</span>
            )}
          </>
        ),
      },
      {
        id: 'debitorClientAccountID',
        header: t('Table.debitorClientAccountID', { defaultValue: 'Debitor Client Account ID' }),
        align: 'left',
        field: 'DebitorClientAccountID',
      },
      {
        id: 'recipientClientAccountID',
        header: t('Table.recipientClientAccountID', { defaultValue: 'Recipient Client Account ID(s)' }),
        align: 'left',
        template: ({ data }) =>
          data.RecipientClientAccountID ??
          (JSON.parse(data.RecipientClientAccountIDSplit) as { ClientAccountID: string }[])
            .map((item) => item.ClientAccountID)
            .join(', '),
      },
      {
        id: 'transferType',
        header: t('Table.transferType', { defaultValue: 'Transfer Type' }),
        align: 'left',
        template: ({ data }) => transferTypeLabelMapper(data.TransferType),
      },
      {
        id: 'frequency',
        header: t('Table.frequency', { defaultValue: 'Frequency' }),
        align: 'left',
        template: ({ data }) => (data.Frequency !== '' ? frequencyLabelMapper(data.Frequency) : ''),
      },
      {
        id: 'amount',
        header: t('Table.amount', { defaultValue: 'Amount' }),
        align: 'left',
        template: ({ data }) => `$ ${formattedCurrency(data.Amount.toString())} ${data.Currency}`,
      },
      {
        id: 'startDate',
        header: t('Table.startDate', { defaultValue: 'Start Date' }),
        align: 'left',
        field: 'ScheduleStartDate',
      },
      {
        id: 'ending',
        header: t('Table.ending', { defaultValue: 'Ending' }),
        align: 'left',
        template: ({ data }) =>
          data.EndingAfterPayments
            ? t('Table.Row.ending', { defaultValue: '{{number}} Payment(s)', number: data.EndingAfterPayments })
            : data.ScheduleEndDate || '',
      },
      {
        id: 'status',
        header: t('Table.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus status={GetColorStatus(data.Status)} label={capitalizeFirstLetterOfWords(data.Status)} />
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item, index) => ({
      id: item.ID || String(index),
      data: item,
    })),
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />

          <TableFiltersContainer.Trigger />
        </Box>

        <TableFiltersContainer.Content>
          <ClientAccountTransfersForm
            frequency={filters.frequency}
            status={filters.status}
            searchTerm={filters.searchTerm}
            transferType={filters.transferType}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            initialTimeRange={[toDayTimestamp(filters.startDate), toDayTimestamp(filters.endDate)]}
            onSearch={(searchParams) => {
              const next: Filters = {
                transferType: searchParams.transferType || '',
                frequency: searchParams.frequency || '',
                searchTerm: searchParams.searchTerm || '',
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                status: searchParams.status || '',
              };

              if (persistFiltersInUrl) {
                updateQueryParams({
                  transferType: next.transferType,
                  frequency: next.frequency,
                  searchTerm: next.searchTerm,
                  startDate: next.startDate,
                  endDate: next.endDate,
                  status: next.status,
                });
              }

              setFilters(next);

              fetchScheduledTransactionHistory({
                ...next,
                hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                offset: setPageIndex(0),
              });
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div className={styles.tableContainer}>{fetching ? <Skeleton height="200px" /> : <Table {...tableProps} />}</div>

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);

              fetchScheduledTransactionHistory({
                ...filters,
                hasInvalidEndDate: filters.startDate > filters.endDate,
                offset,
              });
            }}
          />
        )}
      </Box>

      {!fetching && tableProps.rows && tableProps.rows.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('Table.Messages.noRecordsToDisplay', {
              defaultValue: 'There are no records to display within this date range',
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScheduledClientAccountTransfersTable;
